import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Camera, Upload, X, Image, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PhotoUpload({ 
  photos = [], 
  onPhotosChange, 
  required = false,
  maxPhotos = 10,
  entityType = 'asset',
  entityId = 'temp',
  label = 'Photos',
  helpText = ''
}) {
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const uploadPhoto = async (file) => {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/upload/photos`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    const data = await response.json();
    return data.photos[0]; // Return the first (only) photo URL
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (photos.length + files.length > maxPhotos) {
      toast.error(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    setUploading(true);
    try {
      const uploadPromises = files.map(file => uploadPhoto(file));
      const newPhotoUrls = await Promise.all(uploadPromises);
      onPhotosChange([...photos, ...newPhotoUrls]);
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload photos');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setShowCamera(true);
    } catch (error) {
      toast.error('Could not access camera');
      console.error('Camera error:', error);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const capturePhoto = async () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);

    canvas.toBlob(async (blob) => {
      if (!blob) return;

      if (photos.length >= maxPhotos) {
        toast.error(`Maximum ${maxPhotos} photos allowed`);
        return;
      }

      setUploading(true);
      try {
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        const photoUrl = await uploadPhoto(file);
        onPhotosChange([...photos, photoUrl]);
        toast.success('Photo captured');
      } catch (error) {
        toast.error('Failed to upload captured photo');
      } finally {
        setUploading(false);
      }
    }, 'image/jpeg', 0.85);
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange(newPhotos);
  };

  const getFullUrl = (url) => {
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <span className="text-xs text-gray-500">{photos.length}/{maxPhotos}</span>
      </div>
      
      {helpText && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}

      {/* Camera View */}
      {showCamera && (
        <div className="relative bg-black rounded-lg overflow-hidden">
          <video 
            ref={videoRef} 
            autoPlay 
            playsInline 
            className="w-full h-48 object-cover"
          />
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={capturePhoto}
              disabled={uploading}
              className="bg-white text-black hover:bg-gray-100"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              Capture
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={stopCamera}
            >
              <X className="h-4 w-4" />
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Upload Buttons */}
      {!showCamera && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={startCamera}
            disabled={uploading || photos.length >= maxPhotos}
            className="flex-1"
          >
            <Camera className="h-4 w-4 mr-2" />
            Camera
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || photos.length >= maxPhotos}
            className="flex-1"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Photo Preview Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo, index) => (
            <div key={index} className="relative group aspect-square">
              <img
                src={getFullUrl(photo)}
                alt={`Photo ${index + 1}`}
                className="w-full h-full object-cover rounded-lg border"
              />
              <button
                type="button"
                onClick={() => removePhoto(index)}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {photos.length === 0 && !showCamera && (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
          <Image className="h-8 w-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">
            {required ? 'Add at least one photo to continue' : 'No photos added'}
          </p>
        </div>
      )}

      {required && photos.length === 0 && (
        <p className="text-xs text-red-500">At least one photo is required</p>
      )}
    </div>
  );
}
